import { Injectable, inject } from '@angular/core';
import { USER_SETTINGS_SERVICE } from '@infinitform/platform-core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../config/editor-environment';

export type Theme = 'light' | 'dark' | 'system';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly userSettingsService = inject(USER_SETTINGS_SERVICE, { optional: true });
  private readonly http = inject(HttpClient);
  private currentThemeSubject = new BehaviorSubject<Theme>('system');
  public currentTheme$ = this.currentThemeSubject.asObservable();

  private appliedThemeSubject = new BehaviorSubject<string>('light');
  public appliedTheme$ = this.appliedThemeSubject.asObservable();

  constructor() {
    this.initializeTheme();
    this.listenForStorageChanges();
  }

  // Propagate theme changes made in another window/WebContentsView of the
  // same origin (e.g. cabinet → open editor tabs on desktop). The `storage`
  // event only fires in OTHER documents, so the originating window is unaffected.
  private listenForStorageChanges(): void {
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key !== 'currentTheme' || e.newValue === null) return;
      const theme = e.newValue as Theme;
      if (theme === this.currentThemeSubject.value) return;
      this.currentThemeSubject.next(theme);
      this.applyTheme(theme);
    });
  }

  private initializeTheme() {
    // First load from localStorage for immediate theme application
    const storedTheme = localStorage.getItem('currentTheme') as Theme;
    const theme = storedTheme || 'system';
    this.currentThemeSubject.next(theme);
    this.applyTheme(theme);

    // Desktop is offline-only and has no per-user backend theme store —
    // localStorage is the single source of truth. Skip the HTTP round-trip,
    // which otherwise clobbers the user's choice with the hardcoded IPC default.
    if (environment.IS_DESKTOP) {
      return;
    }

    // Then try to load from backend and sync if different
    this.loadThemeFromBackend()
      .then((backendTheme) => {
        if (backendTheme && backendTheme !== theme) {
          // Backend has different theme, update to match backend
          this.currentThemeSubject.next(backendTheme);
          this.applyTheme(backendTheme);
          localStorage.setItem('currentTheme', backendTheme);
        }
      })
      .catch(() => {
        // If backend fails, keep using localStorage theme
        console.warn('Failed to load theme from backend, using localStorage');
      });
  }

  setTheme(theme: Theme) {
    this.currentThemeSubject.next(theme);
    localStorage.setItem('currentTheme', theme);
    this.applyTheme(theme);
    if (!environment.IS_DESKTOP) {
      this.saveThemeToBackend(theme);
    }
  }

  /** Shows the theme the cabinet chose; the cabinet has already saved it. */
  applyHostTheme(theme: Theme): void {
    if (theme === this.currentThemeSubject.value) return;
    this.currentThemeSubject.next(theme);
    this.applyTheme(theme);
  }

  private applyTheme(theme: Theme) {
    let appliedTheme: string;

    if (theme === 'system') {
      appliedTheme = this.getSystemTheme();
      // Listen for system theme changes
      this.listenForSystemThemeChanges();
    } else {
      appliedTheme = theme;
    }

    this.setDocumentTheme(appliedTheme);
    this.appliedThemeSubject.next(appliedTheme);
  }

  /** Marks <html> with the theme and its colour scheme, so native controls and scrollbars match it. */
  private setDocumentTheme(theme: string): void {
    const root = document.querySelector('html');
    root?.setAttribute('data-theme', theme);
    if (root?.style) root.style.colorScheme = theme === 'dark' ? 'dark' : 'light';
  }

  private getSystemTheme(): string {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private listenForSystemThemeChanges() {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (this.currentThemeSubject.value === 'system') {
        const newTheme = e.matches ? 'dark' : 'light';
        this.setDocumentTheme(newTheme);
        this.appliedThemeSubject.next(newTheme);
      }
    };

    // Remove existing listener if any
    mediaQuery.removeEventListener('change', handleChange);
    // Add new listener
    mediaQuery.addEventListener('change', handleChange);
  }

  getCurrentTheme(): Theme {
    return this.currentThemeSubject.value;
  }

  getAppliedTheme(): string {
    return this.appliedThemeSubject.value;
  }

  private async loadThemeFromBackend(): Promise<Theme | null> {
    try {
      // Through the platform token, not raw HttpClient: every consumer of this
      // endpoint shares one request, and desktop reads it over IPC instead.
      if (!this.userSettingsService) return null;
      const response = await lastValueFrom(this.userSettingsService.get());
      return (response as { theme?: { theme?: Theme } })?.theme?.theme || null;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  private saveThemeToBackend(theme: Theme): void {
    if (!this.userSettingsService) return;
    this.userSettingsService
      .update({ theme: { theme } } as never)
      .subscribe({
        next: () => {
          // Success - theme saved to backend
        },
        error: (error) => {
          console.warn('Failed to save theme to backend:', error);
          // Don't throw error - theme is still applied locally
        }
      });
  }
}
